import React, { useEffect, useState } from "react";
import { Virtuoso } from "react-virtuoso";

import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import useMediaQuery from "@mui/material/useMediaQuery";

import HimsMapComponent from "./hmsMapComponent";
import HimsSystemCard from "./hmsSystemCard";

import { useUserAuth } from "../contexts/authContext";

import { getTimePassedFromIso } from "../util";

const HimsDashboard = () => {
    const [himsSystems, setHimsSystems] = useState([]);
    const [himsFaults, setHimsFaults] = useState([]);

    const [mapPreviewVisible, setMapPreviewVisible] = useState(true);

    const { handleFailedFetch, socket } = useUserAuth();

    const isDesktop = useMediaQuery("(min-width:900px)");

    useEffect(() => {
        getHimsSystems();

        const interval = setInterval(() => {
            getHimsSystems();
        }, 1000 * 30); // 30s

        return () => {
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        socket.on("himsFaultUpdate", (faults) => {
            setHimsFaults(faults);
        });
        socket.emit("getHimsFaults", socket.id);

        return () => {
            socket.off("himsFaultUpdate");
        };
    }, [socket]);

    const getHimsSystems = () => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch("/api/hms/system", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => setHimsSystems(data))
            .catch((err) => handleFailedFetch(err));
    };
    return (
        <Box sx={{ display: "flex", justifyContent: "center", height: "100%" }}>
            <Box sx={{ height: "100%", p: { xs: 1, md: 2 }, width: "100%" }}>
                <Grid container spacing={isDesktop ? 2 : 1} sx={{ height: "100%" }}>
                    <Grid size={himsSystems.length === 1 ? { xs: 12, md: 6 } : { xs: 12, md: 6, xl: 8 }}>
                        <Paper sx={{ p: 2, mb: isDesktop ? 2 : 1, display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                            <Typography variant="h6" noWrap>
                                Health Integrity Monitoring
                            </Typography>

                            <Button size="small" variant="text" onClick={() => setMapPreviewVisible((prev) => !prev)} sx={{ whiteSpace: "nowrap" }}>
                                {mapPreviewVisible ? "Hide Map" : "Show Map"}
                            </Button>
                        </Paper>

                        <Grid container spacing={isDesktop ? 2 : 1} sx={{ flex: 1 }}>
                            {isDesktop === false && mapPreviewVisible === true ? (
                                <Grid size={{ xs: 12 }} sx={{ height: "225px" }}>
                                    <HimsMapComponent />
                                </Grid>
                            ) : (
                                <></>
                            )}
                            {/* Required to prevent row height from expanding when a single card is expanded */}
                            <Grid size={himsSystems.length === 1 ? { xs: 12 } : { xs: 12, xl: 6 }}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: isDesktop ? 2 : 1 }}>
                                    {himsSystems.map((system, i) => (i % 2 === 0 ? <HimsSystemCard key={i} system={system} /> : <React.Fragment key={i} />))}
                                </Box>
                            </Grid>

                            <Grid size={himsSystems.length === 1 ? { xs: 12 } : { xs: 12, xl: 6 }}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: isDesktop ? 2 : 1 }}>
                                    {himsSystems.map((system, i) => (i % 2 === 1 ? <HimsSystemCard key={i} system={system} /> : <React.Fragment key={i} />))}
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid
                        size={himsSystems.length === 1 ? { xs: 12, md: 6 } : { xs: 12, md: 6, xl: 4 }}
                        sx={{ height: isDesktop ? "100%" : "350px", display: "flex", flexDirection: "column", gap: isDesktop ? 2 : 1 }}
                    >
                        {isDesktop === true && mapPreviewVisible === true ? (
                            <Box sx={{ flex: 1 }}>
                                <HimsMapComponent />
                            </Box>
                        ) : (
                            <></>
                        )}
                        <Paper sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1, mb: isDesktop ? "unset" : 1, flex: 1 }}>
                            <Typography variant="h6" noWrap>
                                Recent Faults
                            </Typography>

                            <Box sx={{ flex: 1 }}>
                                <Virtuoso
                                    totalCount={himsFaults.length}
                                    itemContent={(index) => {
                                        return (
                                            <ListItem key={index}>
                                                <ListItemAvatar>
                                                    <Avatar
                                                        sx={{
                                                            bgcolor: himsFaults[index].severity === "HIGH" ? "#CB4C4E" : "#E0D268",
                                                            fontSize: "xx-small"
                                                        }}
                                                    >
                                                        {himsFaults[index].severity}
                                                    </Avatar>
                                                </ListItemAvatar>

                                                <ListItemText
                                                    primary={`${himsFaults[index].component_name} - ${himsFaults[index].name}`}
                                                    secondary={`${getTimePassedFromIso(himsFaults[index].date_reported)} - ${himsFaults[index].description}`}
                                                    slotProps={{ primary: { noWrap: true }, secondary: { color: "text.secondary" } }}
                                                />
                                            </ListItem>
                                        );
                                    }}
                                />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default HimsDashboard;
